import React from 'react';
import { Alert, DataTable, Spinner, StatusBadge, type DataTableColumn } from '@/components/ui';
import {
  formatPcrServiceAreaLabel,
  normalizePcrReportTypeBucket,
  formatPcrStatusLabel,
  type PcrStatus,
  type PcrSubmission,
} from '../../../services/pcrService';
import PcrRowActions from './PcrRowActions';

/** Matches table body text: no primary/underline from Bootstrap link styles */
const tablePlainBtnClass =
  'tw-p-0 tw-border-0 tw-bg-transparent tw-text-content tw-no-underline tw-shadow-none tw-align-baseline';
function formatReceived(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatIso(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'short',
      timeStyle: 'short',
    });
  } catch {
    return iso;
  }
}

function formatCallback(isFeedback: boolean): string {
  return isFeedback ? 'Yes' : 'No';
}

export interface PcrSubmissionsTableProps {
  submissions: PcrSubmission[];
  loading: boolean;
  error: string | null;
  onDismissError?: () => void;
  pcrReplyUiEnabled?: boolean;
  canPcrReply?: boolean;
  canUpdateStatus?: boolean;
  canMoveToInternalReview?: boolean;
  onMoveToInternalReview?: (row: PcrSubmission) => void;
  canMoveToFinalize?: boolean;
  onMoveToFinalize?: (row: PcrSubmission) => void;
  statusTransitioningId?: string | null;
  onReply?: (row: PcrSubmission) => void;
  onEdit?: (row: PcrSubmission) => void;
  onOpenDetail: (row: PcrSubmission) => void;
  formatRoadLabel?: (row: PcrSubmission) => string;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (column: 'createdAt' | 'status' | 'serviceArea' | 'roadName') => void;
  onOpenNotesHistory?: (row: PcrSubmission) => void;
  canOpenNotesHistory?: boolean;
  canPromoteToWorkId?: boolean;
  onPromoteToWorkId?: (row: PcrSubmission) => void;
  canCloseWithoutWork?: boolean;
  onCloseWithoutWork?: (row: PcrSubmission) => void;
  onOpenLinkedWorkId?: (row: PcrSubmission) => void;
  canExportAuditReport?: boolean;
  onExportAuditReport?: (row: PcrSubmission) => void;
  auditReportExportingId?: number | null;
}

const PcrSubmissionsTable: React.FC<PcrSubmissionsTableProps> = ({
  submissions,
  loading,
  error,
  onDismissError,
  canUpdateStatus,
  canMoveToInternalReview,
  onMoveToInternalReview,
  canMoveToFinalize,
  onMoveToFinalize,
  statusTransitioningId,
  pcrReplyUiEnabled,
  canPcrReply,
  onReply,
  onEdit,
  onOpenDetail,
  formatRoadLabel,
  sortBy,
  sortDir,
  onSort,
  onOpenNotesHistory,
  canOpenNotesHistory,
  canPromoteToWorkId,
  onPromoteToWorkId,
  canCloseWithoutWork,
  onCloseWithoutWork,
  onOpenLinkedWorkId,
  canExportAuditReport,
  onExportAuditReport,
  auditReportExportingId,
}) => {
  const hasHistory = Boolean(canOpenNotesHistory && onOpenNotesHistory);
  const showRowActionMenu = Boolean(
    hasHistory ||
      (canExportAuditReport && onExportAuditReport) ||
      (canPcrReply && onReply) ||
      (canUpdateStatus && onEdit) ||
      (canMoveToInternalReview && onMoveToInternalReview) ||
      (canMoveToFinalize && onMoveToFinalize) ||
      (canPromoteToWorkId && onPromoteToWorkId) ||
      (canCloseWithoutWork && onCloseWithoutWork) ||
      (onOpenLinkedWorkId),
  );
  const showTrailingActions = showRowActionMenu;

  if (error) {
    return (
      <Alert variant="danger" className="tw-m-3" dismissible={!!onDismissError} onClose={onDismissError}>
        {error}
      </Alert>
    );
  }

  if (loading && submissions.length === 0) {
    return (
      <div className="tw-flex tw-justify-center tw-p-5">
        <Spinner animation="border" role="status">Loading…</Spinner>
      </div>
    );
  }

  // Mobile card anatomy: PCR ID is the primary identifier (title), Received is the secondary
  // line (subtitle), Status + row actions ride together top-right (trailing) — same corner the
  // desktop actions menu occupies — and the rest fall into the labelled meta grid. Response SLA,
  // Responded and Callback are audit-trail detail that a phone-width triage pass doesn't need;
  // they stay on the desktop table via `mobile: 'hidden'` (dropped from the card only, not the
  // column set).
  const columns: DataTableColumn<PcrSubmission>[] = [
    {
      key: 'pcrId',
      header: 'PCR ID',
      mobile: 'title',
      className: 'tw-whitespace-nowrap',
      cell: (row) => (
        <button
          type="button"
          className={tablePlainBtnClass}
          style={{ fontSize: 'inherit' }}
          onClick={() => onOpenDetail(row)}
          title="Open details"
        >
          {row.report_id || '—'}
        </button>
      ),
    },
    {
      key: 'createdAt',
      header: 'Received',
      sortable: true,
      mobile: 'subtitle',
      className: 'tw-whitespace-nowrap',
      cell: (row) => formatReceived(row.receivedAt),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      mobile: 'trailing',
      cell: (row) => {
        const st = (row.status || 'new') as PcrStatus;
        // The shared status map doesn't have a NEW entry (see report), so this badge falls back
        // to the shared "unknown status" colour; the wording still comes from the PCR-specific
        // labels (New / Internal Review / Finalized) via `label`.
        return <StatusBadge status={st} vocabulary="pcr" label={formatPcrStatusLabel(st)} />;
      },
    },
    {
      key: 'responseSla',
      header: 'Response SLA',
      mobile: 'hidden',
      className: 'tw-text-sm tw-whitespace-nowrap',
      cell: (row) => row.responseTimeBucket || '—',
    },
    {
      key: 'responded',
      header: 'Responded',
      mobile: 'hidden',
      className: 'tw-text-sm tw-whitespace-nowrap',
      cell: (row) => formatIso(row.respondedAt),
    },
    {
      key: 'serviceArea',
      header: 'Service area',
      sortable: true,
      mobileLabel: 'Service area',
      cell: (row) => (row.service_area_text ? formatPcrServiceAreaLabel(row.service_area_text) : '—'),
    },
    {
      key: 'foremanArea',
      header: 'Foreman area',
      mobileLabel: 'Foreman area',
      cell: (row) => (row.foreman_area_name ?? row.community_text) || '—',
    },
    {
      key: 'roadName',
      header: 'Road',
      sortable: true,
      mobileLabel: 'Road',
      cell: (row) => (formatRoadLabel ? formatRoadLabel(row) : row.road_text || '—'),
    },
    {
      key: 'reportType',
      header: 'Report type',
      mobileLabel: 'Report type',
      cell: (row) => normalizePcrReportTypeBucket(row.report_type),
    },
    {
      key: 'activity',
      header: 'Activity',
      mobileLabel: 'Activity',
      cell: (row) => row.activity_type || '—',
    },
    {
      key: 'callback',
      header: 'Callback',
      mobile: 'hidden',
      headerClassName: 'tw-whitespace-nowrap',
      className: 'tw-whitespace-nowrap',
      cell: (row) => formatCallback(Boolean(row.is_feedback)),
    },
    {
      key: 'user',
      header: 'User',
      mobileLabel: 'User',
      cell: (row) => row.user_name || '—',
    },
    {
      key: 'message',
      header: 'Message',
      width: '240px',
      mobileLabel: 'Message',
      className: 'tw-text-sm',
      cell: (row) => (
        <span
          style={{
            display: 'block',
            whiteSpace: 'pre-wrap',
            maxWidth: '420px',
            wordBreak: 'break-word',
            overflowWrap: 'anywhere',
          }}
        >
          {row.message || '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      width: '108px',
      headerClassName: 'tw-whitespace-nowrap',
      className: 'tw-whitespace-nowrap',
      mobile: 'trailing',
      hidden: !showTrailingActions,
      cell: (row) => (
        <PcrRowActions
          row={row}
          pcrReplyUiEnabled={pcrReplyUiEnabled}
          canPcrReply={canPcrReply}
          canUpdateStatus={canUpdateStatus}
          canMoveToInternalReview={canMoveToInternalReview}
          onMoveToInternalReview={onMoveToInternalReview}
          canMoveToFinalize={canMoveToFinalize}
          onMoveToFinalize={onMoveToFinalize}
          statusTransitioningId={statusTransitioningId}
          onReply={onReply}
          onEdit={onEdit}
          onOpenDetail={onOpenDetail}
          canOpenNotesHistory={canOpenNotesHistory}
          onOpenNotesHistory={onOpenNotesHistory}
          canPromoteToWorkId={canPromoteToWorkId}
          onPromoteToWorkId={onPromoteToWorkId}
          canCloseWithoutWork={canCloseWithoutWork}
          onCloseWithoutWork={onCloseWithoutWork}
          onOpenLinkedWorkId={onOpenLinkedWorkId}
          canExportAuditReport={canExportAuditReport}
          onExportAuditReport={onExportAuditReport}
          auditReportExportingId={auditReportExportingId}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={submissions}
      rowKey={(row) => row.id}
      striped
      hover
      sortBy={sortBy}
      sortDir={sortDir}
      onSort={onSort ? (key) => onSort(key as 'createdAt' | 'status' | 'serviceArea' | 'roadName') : undefined}
      emptyMessage="No PCR submissions found."
    />
  );
};

export default PcrSubmissionsTable;
